/**
 * Web activity trace — the twin of `activity-trace.tsx`.
 *
 * One expandable block per assistant turn. The header line is the turn's
 * current state (a shimmering verb while working, a settled summary with the
 * step count and duration once done) and the body is one row per visible tool
 * call, hanging off a hairline rail. It unfolds by itself while work happens,
 * folds when it settles, and stays a click away after that.
 *
 * Every decision — which steps exist, what phase the turn is in, what the
 * header says, whether the body is open — comes from `activity-trace-model.ts`
 * so a conversation cannot narrate itself differently on web and native. This
 * file only draws.
 */
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/icons/MaterialSymbol';
import { HIDDEN_TOOLS } from './tool-cards';
import { formatDuration, runDurationMs, useElapsedLabel, type RunTiming } from './agent-run-timing';
import {
  resolveTraceExpanded,
  resolveTraceHeaderLabel,
  settleStage,
  visibleToolParts,
  type TracePart,
} from './activity-trace-model';
import { useTracePhase } from './use-trace-phase';
import { AgentSparkleIcon } from './agent-sparkle-icon.web';
import { LoadingState } from './loading-state.web';
import { NovaIcon } from './nova-loader.web';
import { NOVA_LABEL_KEYS, resolveNovaStatus } from './nova-loader-motion';
import { ShimmerText } from './shimmer-text.web';
import { resolveActivityCopy, type ActivityTense } from './tool-activity-copy';
import { resolveStepMeta } from './tool-step-meta';

const ROW_STAGGER_MS = 120;

function RowGlyph({ tense }: { tense: ActivityTense }) {
  if (tense === 'running') {
    return <span className="tabbio-trace-row-spinner" aria-hidden />;
  }
  const name = tense === 'failed' ? 'error' : tense === 'waiting' ? 'shield' : 'check';
  return <MaterialSymbol name={name} size={14} />;
}

/** One row of the trace: a real tool call, with its tense-driven glyph and copy. */
export const TraceRow: FC<{
  toolName: string;
  stage?: string;
  result?: unknown;
  isError?: boolean;
  args?: unknown;
  index: number;
}> = ({ toolName, stage, result, isError, args, index }) => {
  const { t } = useTranslation('common');
  // Args let a call name what it is making (slides, not just "a document").
  const copy = resolveActivityCopy({ toolName, stage, result, args, isError, t });
  const meta = resolveStepMeta({ toolName, args });

  return (
    <div
      className="tabbio-trace-row"
      data-state={copy.tense}
      style={{ ['--row-delay' as string]: `${index * ROW_STAGGER_MS}ms` }}
    >
      <span className="tabbio-trace-row-glyph" aria-hidden>
        <RowGlyph tense={copy.tense} />
      </span>
      <span className="tabbio-trace-row-primary" dir="auto">
        {copy.tense === 'running' ? <ShimmerText>{copy.label}</ShimmerText> : copy.label}
      </span>
      {meta ? (
        <span
          className={`tabbio-trace-row-secondary${meta.kind === 'query' ? ' is-mono' : ''}`}
          dir="auto"
          title={meta.text}
        >
          {meta.kind === 'query' ? `“${meta.text}”` : meta.text}
        </span>
      ) : null}
    </div>
  );
};

export const ActivityTrace: FC<{
  parts: readonly TracePart[];
  isRunning: boolean;
  timing: RunTiming;
  /** The running message is a regeneration of an earlier answer. */
  regenerating?: boolean;
}> = ({ parts, isRunning, timing, regenerating = false }) => {
  const { t } = useTranslation('common');
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);

  const phase = useTracePhase(parts, isRunning, HIDDEN_TOOLS);
  const steps = visibleToolParts(parts, HIDDEN_TOOLS);
  const working = phase === 'working' || phase === 'writing';
  const elapsed = useElapsedLabel(timing.startedAt, working, t);
  const novaStatus = resolveNovaStatus({ phase, regenerating });

  // Before anything has arrived there is nothing to fold: the loader alone
  // names the wait. It hands over to the header the moment a step exists.
  if (phase === 'thinking') {
    return (
      <div className="tabbio-trace-block">
        <LoadingState status={novaStatus} label={t(NOVA_LABEL_KEYS[novaStatus])} startedAt={timing.startedAt} />
      </div>
    );
  }

  if (steps.length === 0) return null;

  const expanded = resolveTraceExpanded(phase, manualExpanded);
  const durationMs = runDurationMs(timing);
  // While prose is forming the header wears Nova's writing face and its
  // label; every other phase reads the shared header copy.
  const label =
    phase === 'writing'
      ? t(NOVA_LABEL_KEYS[novaStatus])
      : resolveTraceHeaderLabel({
          phase,
          stepCount: steps.length,
          durationLabel: durationMs != null ? formatDuration(durationMs, t) : null,
          t,
        });

  return (
    <div className="tabbio-trace-block">
      <button
        type="button"
        className="tabbio-trace-header"
        data-working={working ? 'true' : 'false'}
        aria-expanded={expanded}
        onClick={() => setManualExpanded(!expanded)}
      >
        <span className="tabbio-trace-header-icon">
          {phase === 'writing' ? <NovaIcon status={novaStatus} size={16} /> : <AgentSparkleIcon size={16} />}
        </span>
        <span role="status" className="tabbio-trace-header-label" dir="auto">
          {working ? (
            <ShimmerText>{label}</ShimmerText>
          ) : (
            <span key={label} className="is-settled">
              {label}
            </span>
          )}
        </span>
        {working && elapsed ? (
          <span className="tabbio-elapsed" dir="ltr">
            {elapsed}
          </span>
        ) : null}
        <span className={`tabbio-trace-chevron${expanded ? ' is-open' : ''}`} aria-hidden>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      <div className={`tabbio-trace-body${expanded ? ' is-open' : ''}`} aria-hidden={!expanded}>
        <div>
          <div className="tabbio-trace-rail">
            <div className="tabbio-trace-rows">
              {steps.map((part, index) => (
                <TraceRow
                  key={`step-${part.toolCallId ?? index}`}
                  toolName={part.toolName!}
                  stage={settleStage(part.stage, isRunning)}
                  result={part.result}
                  isError={part.isError}
                  args={(part as { args?: unknown }).args}
                  index={index}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
